import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { ApiService } from '@core/providers/http';
import { map, Observable } from 'rxjs';
import { ConfiguracaoEnum } from '../models/configuracoes.model';
import { TipoDadoEnum } from '../models/tipoDadoEnum.model';
import { PagedResponse } from '../models/reponse.model';
import { ConfiguracaoGrupo } from '../models/configuracaoGrupo.model';
import { ConfiguracaoGrupoResponse } from '../models/configuracaoGrupoResponse.model';

@Injectable({
  providedIn: 'root'
})
export class ConfiguracoesService {
  public params = new HttpParams();

  constructor(private readonly apiService: ApiService) { }

  /**
   * Busca configurações por tipo geral (Doação, Empréstimo, etc)
   */
  public list(
    configuracoesGeralENUM: number
  ): Observable<ConfiguracaoGrupo> {

    const params = new HttpParams()
      .set('configuracoesGeralENUM', configuracoesGeralENUM);

    return this.apiService
      .get<PagedResponse<ConfiguracaoGrupoResponse>>(
        `/Configuracoes/Dominio`,
        params
      )
      .pipe(
        map(response => {

          if (response instanceof Blob) {
            throw new TypeError('Resposta inesperada: Blob');
          }

          if (!response?.data?.length) {
            throw new Error('Grupo de configuração não encontrado');
          }

          const grupo = response.data[0];

          return {
            configuracoesGeralENUM: grupo.configuracoesGeralENUM,
            configuracaoParametros: grupo.configuracaoParametros.map(p => ({
              configuracoesEspecificaENUM: p.configuracoesEspecificaENUM,
              tipoDadoENUM: p.tipoDadoENUM,
              valor: this.parseValor(p.valor, p.tipoDadoENUM)
            }))
          };
        })
      )
  }

  /**
   * Atualiza configurações por tipo
   */
  public updateByTipo(
    payload: ConfiguracaoGrupo[]
  ): Observable<any> {
    return this.apiService.put(`/Configuracoes`, payload);
  }

  /**
   * Converte valor conforme tipo
   */
  private parseValor(
    valor: string,
    tipo: TipoDadoEnum
  ): any {

    switch (tipo) {
      case TipoDadoEnum.Int:
        return Number(valor);

      case TipoDadoEnum.Decimal:
        return Number(valor);

      case TipoDadoEnum.Boolean:
        return valor === 'true';

      case TipoDadoEnum.Date:
        return new Date(valor);

      default:
        return valor;
    }
  }

  /**
     * Busca configuração pelo EnumGeral e pela EnumEspecifica
     */
  public getByEnum<T>(
    configuracaoGeral: number,
    enumValue: ConfiguracaoEnum
  ): Observable<T> {

    return this.list(configuracaoGeral).pipe(
      map(grupo => {

        const config = grupo.configuracaoParametros
          .find(c => c.configuracoesEspecificaENUM === enumValue);

        if (!config) {
          throw new Error(
            `Configuração ${enumValue} não encontrada`
          );
        }

        return config.valor as T;
      })
    );
  }

  // Parâmetros decimais
  public getDoacaoMinima(configuracaoGeral: number): Observable<number> {
    return this.getByEnum<number>(configuracaoGeral, ConfiguracaoEnum.DoacaoMinima);
  }

  public getDoacaoMaxima(configuracaoGeral: number): Observable<number> {
    return this.getByEnum<number>(configuracaoGeral, ConfiguracaoEnum.DoacaoMaxima);
  }

  public getTaxaServico(configuracaoGeral: number): Observable<number> {
    return this.getByEnum<number>(configuracaoGeral, ConfiguracaoEnum.TaxaServico);
  }

  public getComissaoCampanha(configuracaoGeral: number): Observable<number> {
    return this.getByEnum<number>(configuracaoGeral, ConfiguracaoEnum.ComissaoPorCampanha);
  }

  // Parâmetros booleanos
  public isFinanciamentoDoacaoAtivo(configuracaoGeral: number): Observable<boolean> {
    return this.getByEnum<boolean>(configuracaoGeral, ConfiguracaoEnum.FinanciamentoDoacao);
  }

  public isFinanciamentoEmprestimoAtivo(configuracaoGeral: number): Observable<boolean> {
    return this.getByEnum<boolean>(configuracaoGeral, ConfiguracaoEnum.FinanciamentoEmprestimo);
  }

  public isFinanciamentoRecompensaAtivo(configuracaoGeral: number): Observable<boolean> {
    return this.getByEnum<boolean>(configuracaoGeral, ConfiguracaoEnum.FinanciamentoRecompensa);
  }

  // Parâmetros numéricos
  public getDuracaoMinima(configuracaoGeral: number): Observable<number> {
    return this.getByEnum<number>(configuracaoGeral, ConfiguracaoEnum.DuracaoMinima);
  }

  public getDuracaoMaxima(configuracaoGeral: number): Observable<number> {
    return this.getByEnum<number>(configuracaoGeral, ConfiguracaoEnum.DuracaoMaxima);
  }

  public getQtdProjetosCriador(configuracaoGeral: number): Observable<number> {
    return this.getByEnum<number>(configuracaoGeral, ConfiguracaoEnum.QtdProjetosCriador);
  }

  public getPrazoAlerta(configuracaoGeral: number): Observable<number> {
    return this.getByEnum<number>(configuracaoGeral, ConfiguracaoEnum.PrazoAlerta);
  }

  public getAprovacaoCampanha(configuracaoGeral: number): Observable<number> {
    return this.getByEnum<number>(configuracaoGeral, ConfiguracaoEnum.AprovacaoCampanha);
  }

  public getCampanhaBemSucedida(configuracaoGeral: number): Observable<number> {
    return this.getByEnum<number>(configuracaoGeral, ConfiguracaoEnum.CampanhaBemSucedida);
  }

  public getLimiteDoacaoPorCampanha(configuracaoGeral: number): Observable<number> {
    return this.getByEnum<number>(configuracaoGeral, ConfiguracaoEnum.LimiteDoacaoPorCampanha);
  }

  public getDoacaoNaoPermitida(configuracaoGeral: number): Observable<number> {
    return this.getByEnum<number>(configuracaoGeral, ConfiguracaoEnum.DoacaoNaoPermitida);
  }

}